import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { RootState } from '../store/store';
import { collectionsService } from '../services/collectionsService';
import { statsService } from '../services/statsService';
import { setCollections } from '../store/collectionsSlice';
import { PlusIcon, BookOpenIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const MAX_FREE_COLLECTIONS = 5;

interface UserStats {
  collectionsCreated?: number;
}

export const QuizList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const collections = useSelector((state: RootState) => state.collections.collections);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [stats, setStats] = useState<UserStats | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const fetchData = async () => {
      try {
        const [collectionsData, statsData] = await Promise.all([
          collectionsService.getCollections(),
          statsService.getUserStats()
        ]);
        dispatch(setCollections(collectionsData));
        setStats(statsData);
      } catch (error: any) {
        setError(error.response?.data?.message || 'Error fetching data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user, dispatch, navigate]);

  const handleCreateCollection = () => {
    const createdCount = stats?.collectionsCreated ?? 0;
    if (!user?.isPremium && createdCount >= MAX_FREE_COLLECTIONS) {
      setShowUpgradeModal(true);
    } else {
      navigate('/quiz/create');
    }
  };

  const handleDeleteCollection = async (collectionId: string) => {
    try {
      await collectionsService.deleteCollection(collectionId);
      dispatch(setCollections(collections.filter(c => c._id !== collectionId)));
      toast.success('Collection deleted successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error deleting collection');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <NavigationBar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <NavigationBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Your Collections</h1>
            {!user?.isPremium && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {collections.length}/5 collections used
              </p>
            )}
          </div>
          <button
            onClick={handleCreateCollection}
            disabled={!user?.isPremium && collections.length >= 5}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Collection
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {collections.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No collections</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new collection
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/quiz/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Collection
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {collections.map((collection) => (
              <motion.div
                key={collection._id}
                whileHover={{ y: -4 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {collection.title}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                  {collection.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <BookOpenIcon className="h-4 w-4 mr-1" />
                    <span>{collection.flashcardCount || 0} cards</span>
                    {!user?.isPremium && collection.flashcardCount >= 10 && (
                      <span className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded-full">
                        Limit reached
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => navigate(`/collections/${collection._id}`)}
                    className="flex-1 px-4 py-2 text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => navigate(`/quiz/${collection._id}`)}
                    className="flex-1 px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 dark:from-indigo-600 dark:to-purple-700 dark:hover:from-indigo-700 dark:hover:to-purple-800"
                  >
                    Practice
                  </button>
                  <button
                    onClick={() => handleDeleteCollection(collection._id)}
                    className="px-4 py-2 text-sm font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30 dark:hover:bg-red-900/50"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Upgrade to Premium
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              You've reached the limit of 5 collections for free users. Upgrade to Premium to create unlimited collections and unlock more features!
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Maybe Later
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-700"
              >
                View Plans
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};